/*
 * ErrorLogViewErrorList.tsx  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useState } from "react";
import { BEM } from "@adapptio/lib-components";
import { ErrorList } from "../../../../ui/components/ErrorList/ErrorList";
import { ERROR_LIST_CATEGORY } from "../../../../ui/components/ErrorList/ErrorListTypes";
import { DOC_ERROR_SEVERITY } from "@adapptio/lib-blueprint";
import { ErrorLogView } from "../ErrorLogView";
import { useSimpleEvent } from "../../../../ui/hooks/useSimpleEvent";
import { IErrorGroup } from "../../../../errors/ErrorsTypes";

export interface IErrorLogViewErrorListProps {
	view: ErrorLogView;
	className?: string;
	severityFilter?: Set<DOC_ERROR_SEVERITY>;
}

export const ErrorLogViewErrorsList: React.FC<IErrorLogViewErrorListProps> = ({
	view,
	className,
	severityFilter
}) => {
	const [ errors, setErrors ] = useState<IErrorGroup[]>(() => view.getErrors());
	const baseClass = BEM("edt-error-log-view-errors-list");
	baseClass.add("edt-base");

	if (className) {
		baseClass.add(className);
	}

	useSimpleEvent(view.onErrorsChanged, () => {
		setErrors(view.getErrors());
	});

	const handleErrorClick = useCallback((error, errorGroup) => {
		view.openErrorDocument(errorGroup);
	}, []);

	return (
		<div className={baseClass.toClassName()}>
			<ErrorList
				errorGroups={errors}
				category={ERROR_LIST_CATEGORY.ALL}
				severityFilter={severityFilter}
				onCompileErrorClick={handleErrorClick}
				onParseErrorClick={handleErrorClick}
				onRuntimeErrorClick={handleErrorClick}
			/>
		</div>
	);
};
